import { Entity } from '@square/ignition';
import { ReferenceHrefBuilders } from '@squareup/dex-tech-utils-routing';
import { Link, NativeButton } from '@squareup/dex-ui';
import { Box, Paragraph30 } from '@squareup/dex-ui-shared-base';
import { MarketHeader, MarketButton } from '@squareup/dex-ui-shared-market';
import { useParams } from '@squareup/dex-utils-shared-routing-hooks';
import { breakHtmlForCamelCaseReact } from '@squareup/dex-utils-text';
import clsx from 'clsx';
import React, { FunctionComponent, Ref, useCallback, useMemo } from 'react';

import { ReferenceObject } from '../../ReferenceObject/src/ReferenceObject';

import style from './reference-object-modal-contents.module.css';

interface ReferenceObjectModalContentsProps {
  entity: Entity;
  breadcrumbs: Entity[];
  onBreadcrumbClicked: (index: number) => void;
  onLinkAwayAction: () => void;
  mainRef: Ref<HTMLElement>;
}

const ReferenceObjectModalContents: FunctionComponent<
  ReferenceObjectModalContentsProps
> = ({
  entity,
  breadcrumbs,
  onBreadcrumbClicked,
  onLinkAwayAction,
  mainRef,
}) => {
  // TODO: How to avoid using the router so deep in the component tree?
  const query = useParams();

  const href = useMemo(() => {
    function buildHref(ent: Entity) {
      if (ent.isEnum) {
        return ReferenceHrefBuilders.versionEnum({
          square_version: query?.square_version as string,
          enum: ent.name,
        });
      } else {
        return ReferenceHrefBuilders.versionObject({
          square_version: query?.square_version as string,
          object: ent.name,
        });
      }
    }

    return buildHref(entity);
  }, [entity, query?.square_version]);

  const Breadcrumb: FunctionComponent<{
    entity: Entity;
    index: number;
    disabled?: boolean;
  }> = ({ entity: ent, index, disabled = false }) => {
    const onClickMemo = useCallback(() => {
      !disabled && onBreadcrumbClicked(index);
    }, [disabled, index]);

    return (
      <NativeButton
        testId={`open-breadcrumb-${ent.name}`}
        trackingId={`open-breadcrumb-${ent.name}`}
        className={clsx(style.breadcrumb, disabled && style.disabled)}
        onClick={onClickMemo}
      >
        <Paragraph30
          className={clsx(style['breadcrumb-text'], disabled && style.disabled)}
        >
          {breakHtmlForCamelCaseReact(ent.name)}
        </Paragraph30>
      </NativeButton>
    );
  };

  return (
    <>
      <MarketHeader>
        <Box slot="actions">
          <Link
            testId="object-modal-open-page"
            trackingId="navigate-to-open-reference-object-another-page"
            href={href}
            onClick={onLinkAwayAction}
          >
            <MarketButton
              trackingId="navigate-to-open-reference-object-another-page"
              rank="secondary"
              size="small"
            >
              Open in page
            </MarketButton>
          </Link>
        </Box>
      </MarketHeader>
      <Box as="main" ref={mainRef}>
        <ReferenceObject
          compact={true}
          disableAnchors={true}
          entity={entity}
          eyebrow={entity.isEnum ? 'Enum' : 'Object'}
          breadcrumbs={
            breadcrumbs.length > 0 && (
              <>
                {breadcrumbs.map((breadcrumb, i) => {
                  return (
                    <>
                      <Breadcrumb
                        key={i}
                        index={i}
                        entity={breadcrumb}
                      ></Breadcrumb>
                      <Paragraph30
                        margin={{ horizontal: '0.5x' }}
                        colorVariant="30"
                      >
                        /
                      </Paragraph30>
                    </>
                  );
                })}
                <Breadcrumb
                  index={breadcrumbs.length}
                  entity={entity}
                  disabled={true}
                ></Breadcrumb>
              </>
            )
          }
        />
      </Box>
    </>
  );
};

export { ReferenceObjectModalContents };
